import { IkeGeodesic } from '@ikegps/core-measurement';
import type { LocationData } from '@ikegps/core-api';
import {
	type CollectionModel,
	CollectionModelFieldAnchor,
	CollectionModelFieldLocation,
	CollectionModelFieldMidSpan,
	CollectionModelFieldSubFormInstance,
	CollectionModelFieldVector,
	getSpanRole,
	type SpanType,
} from '@ikegps/core-shared';
import { omit } from 'lodash';
import { action, observable, makeObservable } from 'mobx';
import { AppStore } from '../../store';
import { getSpanType, type MaybeSpanType } from './map.line/map.line';
import type { PoleAccuracy } from './map.point/map.point';

export interface PointCollectionLineInfo {
	field: CollectionModelFieldVector | CollectionModelFieldAnchor;
	target: LocationData;
	source: LocationData;
	/** Line kind (drives color/label class); pure function of the field's schema, cached with the vector. */
	kind: MaybeSpanType;
	/** Normalized span role (fore/back/other) when the vector belongs to a span subform, else null. */
	spanRole: SpanType | null;
}

function resolveSpanRole(field: CollectionModelFieldVector): SpanType | null {
	return field.parent instanceof CollectionModelFieldSubFormInstance ? getSpanRole(field.parent) : null;
}

class Cacheable<T extends {}> {
	@observable private cache: {
		[key: string]: T;
	} = {};

	constructor() {
		makeObservable(this);
	}

	@action push(key: string, value: T, ttlMs?: number) {
		this.cache[key] = value;
	}

	@action flush() {
		this.cache = {};
	}

	@action drop(key: string) {
		this.cache = omit(this.cache, key);
	}

	read(key: string): T | null {
		return this.cache[key] || null;
	}

	view(transformer: (k: string) => T | null) {
		return (key: string) => {
			const existing = this.read(key);

			if (existing) {
				return existing;
			}

			const result = transformer(key);

			if (result) {
				this.push(key, result);
			}

			return this.read(key);
		};
	}

	all() {
		return Object.freeze({ ...this.cache });
	}
}

export interface PointCollectionMidSpanDot {
	captureId: string;
	/** The laser strike point on the wire — `capture.target`, NOT the pole location. */
	target: LocationData;
	/** The MidSpan field's display name, shown as the dot's hover title. */
	displayName: string;
}

export class TopologicalPointCollectionCache {
	private static vectors = new Cacheable<PointCollectionLineInfo[]>();
	private static locations = new Cacheable<LocationData>();
	private static poleAccuracy = new Cacheable<PoleAccuracy>();
	private static midSpanDots = new Cacheable<PointCollectionMidSpanDot[]>();

	static getCollectionVectors(collectionOrRevision: CollectionModel | string): PointCollectionLineInfo[] {
		// Handle both CollectionModel and string (revision) inputs
		const collection =
			typeof collectionOrRevision === 'string' ? AppStore.Collection.get(collectionOrRevision) : collectionOrRevision;

		if (!collection) {
			return [];
		}

		// Check cache first
		const cached = TopologicalPointCollectionCache.vectors.read(collection.revision);
		if (cached) {
			return cached;
		}

		const source = collection.location;
		if (!source) {
			return [];
		}

		const output: PointCollectionLineInfo[] = [];

		collection.forEachField((field) => {
			if (!field.value) {
				return;
			}

			if (CollectionModelFieldVector.isVectorField(field)) {
				const value = field.value;
				const target = IkeGeodesic.projectLocation(source, value.bearing, value.distance);
				output.push({ field, source, target, kind: getSpanType(field), spanRole: resolveSpanRole(field) });
			}

			if (CollectionModelFieldAnchor.isAnchorField(field)) {
				const distanceBearingPitch = field.distanceBearingPitch(source);
				if (distanceBearingPitch == null) {
					return;
				}
				const { distance, bearing } = distanceBearingPitch;

				if (distance === 0) {
					return;
				}

				const target = IkeGeodesic.projectLocation(source, bearing, distance);
				if (target && target.latitude && target.longitude) {
					output.push({ field, source, target, kind: getSpanType(field), spanRole: null });
				}
			}
		});

		// Cache the result
		TopologicalPointCollectionCache.vectors.push(collection.revision, output);

		return output;
	}

	static getCollectionLocation(collectionOrRevision: CollectionModel | string): LocationData | null {
		// Handle both CollectionModel and string (revision) inputs
		const collection =
			typeof collectionOrRevision === 'string' ? AppStore.Collection.get(collectionOrRevision) : collectionOrRevision;

		if (!collection) {
			return null;
		}

		// Check cache first
		const cached = TopologicalPointCollectionCache.locations.read(collection.revision);
		if (cached) {
			return cached;
		}

		// Get location and cache it
		const location = collection.location;
		if (location) {
			TopologicalPointCollectionCache.locations.push(collection.revision, location);
		}

		return location;
	}

	static getCollectionPoleAccuracy(collectionOrRevision: CollectionModel | string): PoleAccuracy {
		// Handle both CollectionModel and string (revision) inputs
		const collection =
			typeof collectionOrRevision === 'string' ? AppStore.Collection.get(collectionOrRevision) : collectionOrRevision;

		if (!collection) {
			return 'default';
		}

		// Check cache first
		const cached = TopologicalPointCollectionCache.poleAccuracy.read(collection.revision);
		if (cached) {
			return cached;
		}

		// Calculate pole accuracy
		const field = collection.locationModelField;
		let poleAccuracy: PoleAccuracy = 'default';

		if (CollectionModelFieldLocation.isLocationField(field)) {
			switch (field.provider) {
				case 'truesize':
					poleAccuracy = 'low';
					break;
				case 'gps':
					const accuracy = field.value.accuracy ?? 0;

					if (field.raw.fix === 'rtkfixed') {
						poleAccuracy = accuracy > 0.1 ? 'rtk_float' : 'rtk_fixed';
					} else if (field.raw.fix === 'rtkfloat') {
						poleAccuracy = accuracy > 1 ? 'low' : 'rtk_float';
					} else {
						poleAccuracy = 'low';
					}
					break;
				default:
					poleAccuracy = field.provider;
			}
		} else {
			poleAccuracy = 'low';
		}

		// Cache the result
		TopologicalPointCollectionCache.poleAccuracy.push(collection.revision, poleAccuracy);

		return poleAccuracy;
	}

	/**
	 * MidSpan dot data per revision (UFO-1082 review): the recursive field walk + capture resolution used to
	 * re-run on every overlay render. The cache is only populated once every midspan field's captures have
	 * resolved — reading a capture both triggers its fetch and registers the MobX dependency, so caching a
	 * partial walk would freeze the dots out and stop the fetch from retrying. Dots derive from
	 * `capture.target`, so pole relocation does not invalidate them; capture changes within an unsaved
	 * revision are accepted staleness (the revision changes on save).
	 */
	static getCollectionMidSpanDots(collection: CollectionModel): PointCollectionMidSpanDot[] {
		const cached = TopologicalPointCollectionCache.midSpanDots.read(collection.revision);
		if (cached) {
			return cached;
		}

		const dots: PointCollectionMidSpanDot[] = [];
		let complete = true;

		collection.forEachField((field) => {
			if (!CollectionModelFieldMidSpan.isMidSpanField(field)) {
				return;
			}
			if (field.getCaptureIds().length === 0) {
				return; // Legitimately empty — no dot, and nothing left to resolve.
			}
			if (field.getCaptures().length !== field.getCaptureIds().length) {
				// `getCaptures()` drops both the not-yet-loaded and the permanently failed. Only an in-flight
				// fetch is worth walking again for: a capture that errored (deleted, 404) never resolves, and
				// treating it as "still loading" would re-run this walk on every pan and zoom forever.
				const stillFetching = field.getCaptureIds().some((id) => {
					const fetchable = collection.state.loader.Capture.getById(id);
					return fetchable.value == null && fetchable.error == null;
				});

				if (stillFetching) {
					complete = false;
					return;
				}
				// Everything missing has errored. A span shot that did load still gets its dot below.
			}

			// The span shot only: the ground reference was taken from the device position, not the wire.
			const capture = field.spanCapture;
			if (capture?.target == null) {
				return; // Fully resolved but no strike-point GPS — permanently no dot.
			}
			dots.push({ captureId: capture.objectId, target: capture.target, displayName: field.schema.displayName });
		});

		if (complete) {
			TopologicalPointCollectionCache.midSpanDots.push(collection.revision, dots);
		}
		return dots;
	}

	static dropCollectionVectors(revision: string) {
		TopologicalPointCollectionCache.vectors.drop(revision);
	}

	static dropCollectionLocations(revision: string) {
		TopologicalPointCollectionCache.dropCollectionVectors(revision);
		TopologicalPointCollectionCache.locations.drop(revision);
		TopologicalPointCollectionCache.dropCollectionPoleAccuracy(revision);
		TopologicalPointCollectionCache.dropCollectionMidSpanDots(revision);
	}

	static dropCollectionMidSpanDots(revision: string) {
		TopologicalPointCollectionCache.midSpanDots.drop(revision);
	}

	static dropCollectionPoleAccuracy(revision: string) {
		TopologicalPointCollectionCache.poleAccuracy.drop(revision);
	}

	static addVector(sourceRevision: string, field: CollectionModelFieldVector) {
		const source = TopologicalPointCollectionCache.getCollectionLocation(sourceRevision);
		if (source == null) {
			return;
		}

		const {
			value: { bearing, distance },
		} = field;

		const target = IkeGeodesic.projectLocation(source, bearing, distance);

		const vectors = TopologicalPointCollectionCache.getCollectionVectors(sourceRevision);
		vectors.push({ field, source, target, kind: getSpanType(field), spanRole: resolveSpanRole(field) });
		TopologicalPointCollectionCache.vectors.push(sourceRevision, vectors);
	}

	static flushVectors() {
		this.vectors.flush();
	}
}
